import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Clock, Calendar, ChevronRight, LogIn, Home } from "lucide-react";
import { format, subDays, isAfter } from "date-fns";
import dcLogo from "@/assets/dc-logo.png";
import { isNativePlatform } from "@/lib/platform";
import { Seo } from "@/components/Seo";

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  category_id: string | null;
  author_name: string;
  is_published: boolean;
  published_at: string | null;
  reading_time_minutes: number;
  created_at: string;
  blog_categories?: BlogCategory | null;
}

const Blog = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const activeCategory = searchParams.get("category") || "all";

  useEffect(() => {
    loadData();
    supabase.auth.getSession().then(({ data }) => setIsLoggedIn(!!data.session));
  }, []);

  const loadData = async () => {
    try {
      const [catRes, postRes] = await Promise.all([
        supabase.from("blog_categories").select("*").order("sort_order"),
        supabase
          .from("blog_posts")
          .select("*, blog_categories(*)")
          .eq("is_published", true)
          .order("published_at", { ascending: false }),
      ]);
      setCategories(catRes.data || []);
      setPosts((postRes.data as any[]) || []);
    } catch (e) {
      console.error("Failed to load blog data", e);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts =
    activeCategory === "all"
      ? posts
      : activeCategory === "whats-new"
        ? posts.filter((p) => {
            const sevenDaysAgo = subDays(new Date(), 7);
            const publishedDate = p.published_at ? new Date(p.published_at) : new Date(p.created_at);
            return isAfter(publishedDate, sevenDaysAgo);
          })
        : posts.filter((p) => {
            const cat = categories.find((c) => c.slug === activeCategory);
            return cat && p.category_id === cat.id;
          });

  const featuredPost = filteredPosts[0];
  const gridPosts = filteredPosts.slice(1);

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return null;
    return categories.find((c) => c.id === categoryId)?.name || null;
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Interior Design Blog — AI Tips, Trends & Room Inspiration | Decor Canvas"
        description="Interior design ideas, AI room makeover tutorials, style guides, and trend reports from Decor Canvas. Scandinavian, minimalist, modern, Indian and more."
        canonicalPath="/blog"
      />
      {/* Header — mobile mirrors Notifications screen exactly */}
      {isNativePlatform() ? (
        <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10 safe-area-inset-top">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-xl sm:text-2xl font-bold">Blog</h1>
              </div>
              <Button variant="ghost" size="icon" onClick={() => navigate("/designer")} aria-label="Home">
                <Home className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>
      ) : (
        <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img src={dcLogo} alt="Decor Canvas" className="h-8 w-8 rounded-lg" />
              <span className="text-lg font-bold tracking-tight text-foreground">Decor Canvas</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              {isLoggedIn ? (
                <Button size="sm" onClick={() => navigate("/designer")} className="bg-primary text-primary-foreground">
                  <Home className="h-4 w-4 mr-1" />
                  Home
                </Button>
              ) : (
                <Button size="sm" onClick={() => navigate("/auth")} className="bg-primary text-primary-foreground">
                  <LogIn className="h-4 w-4 mr-1" />
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </header>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground tracking-tight mb-3">
            Blog
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Interior design inspiration, tips & trends
          </p>
        </div>

        {/* Category Filter Bar */}
        <div className="mb-10">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide bg-muted/50 rounded-2xl px-3 py-2 border border-border/50">
            <button
              onClick={() => setSearchParams({})}
              className={`whitespace-nowrap px-5 py-2 rounded-xl text-sm font-semibold transition-all shrink-0 ${
                activeCategory === "all"
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "text-muted-foreground hover:text-foreground hover:bg-background"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSearchParams({ category: "whats-new" })}
              className={`whitespace-nowrap px-5 py-2 rounded-xl text-sm font-semibold transition-all shrink-0 ${
                activeCategory === "whats-new"
                  ? "bg-primary text-primary-foreground shadow-md"
                  : "text-muted-foreground hover:text-foreground hover:bg-background"
              }`}
            >
              What's New
            </button>
            {categories.filter((cat) => cat.slug !== "whats-new").map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSearchParams({ category: cat.slug })}
                className={`whitespace-nowrap px-5 py-2 rounded-xl text-sm font-semibold transition-all shrink-0 ${
                  activeCategory === cat.slug
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "text-muted-foreground hover:text-foreground hover:bg-background"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="space-y-8">
            <Skeleton className="w-full h-[400px] rounded-2xl" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-[320px] rounded-2xl" />
              ))}
            </div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-muted-foreground">No posts yet. Check back soon!</p>
          </div>
        ) : (
          <>
            {/* Featured Post */}
            {featuredPost && (
              <div
                className="relative rounded-2xl overflow-hidden mb-10 cursor-pointer group"
                onClick={() => navigate(`/blog/${featuredPost.slug}`)}
              >
                <div className="aspect-[4/5] sm:aspect-[21/8]">
                  {featuredPost.cover_image_url ? (
                    <img
                      src={featuredPost.cover_image_url}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20" />
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent sm:via-black/30" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-10">
                  {getCategoryName(featuredPost.category_id) && (
                    <Badge className="mb-2 sm:mb-3 bg-primary/90 text-primary-foreground border-0">
                      {getCategoryName(featuredPost.category_id)}
                    </Badge>
                  )}
                  <h2 className="text-xl sm:text-4xl font-bold text-white mb-2 sm:mb-3 leading-tight">
                    {featuredPost.title}
                  </h2>
                  {featuredPost.excerpt && (
                    <p className="hidden sm:block text-white/80 text-sm sm:text-base max-w-2xl line-clamp-2 mb-4">
                      {featuredPost.excerpt}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-white/60 text-xs sm:text-sm">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {featuredPost.reading_time_minutes} min read
                    </span>
                    {featuredPost.published_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {format(new Date(featuredPost.published_at), "MMM d, yyyy")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Grid */}
            {gridPosts.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {gridPosts.map((post) => (
                  <article
                    key={post.id}
                    className="group rounded-2xl overflow-hidden border border-border bg-card shadow-card hover:shadow-elegant transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/blog/${post.slug}`)}
                  >
                    <div className="aspect-[16/10] overflow-hidden relative">
                      {post.cover_image_url ? (
                        <img
                          src={post.cover_image_url}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent/10" />
                      )}
                      {getCategoryName(post.category_id) && (
                        <Badge className="absolute top-3 left-3 bg-background/90 text-foreground border-0 text-xs backdrop-blur-sm">
                          {getCategoryName(post.category_id)}
                        </Badge>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                          {post.excerpt}
                        </p>
                      )}
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {post.reading_time_minutes} min
                          </span>
                          {post.published_at && (
                            <span>{format(new Date(post.published_at), "MMM d, yyyy")}</span>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Blog;
